import { TaskStatus, TaskType } from "@common/types/task";
import type {
    BoardAddTarget,
    BoardMove,
    BoardMoveVerdict,
    BoardPlaceCheck,
} from "@design-system/Board/Board.types";
import type { DemoTask } from "./fixtures/demoTasks";
import {
    findTaskPlaceNeighbour,
    TASK_PLACE_GROUP_LABELS,
    TASK_SORT_INHERITANCE,
    TASK_SORT_PLACE_GROUPS,
    type TaskSortKey,
} from "./taskBoardSort";
import { updateMovedTask, type TaskBoardView } from "./taskBoardView";

interface InsertTaskArgs {
    tasks: readonly DemoTask[];
    task: DemoTask;
    neighbours: BoardNeighbours;
    /** Where the task goes when neither neighbour is in the list. */
    fallbackIndex: number;
}

/** Puts `task` beside the board's visible neighbours, so tasks hidden by filters keep their place. */
const insertTaskBetween = ({
    tasks,
    task,
    neighbours,
    fallbackIndex,
}: InsertTaskArgs) => {
    const remainingTasks = tasks.filter(({ id }) => id !== task.id);
    const afterIndex = remainingTasks.findIndex(
        ({ id }) => id === neighbours.afterItemId,
    );
    const beforeIndex = remainingTasks.findIndex(
        ({ id }) => id === neighbours.beforeItemId,
    );
    let index = Math.min(fallbackIndex, remainingTasks.length);
    if (afterIndex !== -1) {
        index = afterIndex;
    } else if (beforeIndex !== -1) {
        index = beforeIndex + 1;
    }
    remainingTasks.splice(index, 0, task);
    return remainingTasks;
};

type BoardNeighbours = Pick<BoardAddTarget, "beforeItemId" | "afterItemId">;
type TaskSortKeyInheritance = (
    neighbour: DemoTask,
    task: DemoTask,
) => Partial<TaskSortKey>;

const findTask = (tasks: readonly DemoTask[], id: string | null) =>
    tasks.find((task) => task.id === id) ?? null;

/**
 * For `task` placed in a sorted gap: the sort key it copies from
 * `neighbour`, one of the gap's `placeNeighbours`, and the side of
 * `neighbours` it lands on, so it ties with that card and shows in the gap.
 */
const placeBeside = ({
    neighbour,
    task,
    placeNeighbours,
    neighbours,
    inheritSortKey,
}: {
    neighbour: DemoTask | null;
    task: DemoTask;
    placeNeighbours: BoardNeighbours;
    neighbours: BoardNeighbours;
    inheritSortKey: TaskSortKeyInheritance;
}) => {
    if (neighbour === null) {
        return undefined;
    }
    const isBelowNeighbour = neighbour.id === placeNeighbours.beforeItemId;
    return {
        sortKey: inheritSortKey(neighbour, task),
        neighbours: {
            beforeItemId: isBelowNeighbour ? neighbours.beforeItemId : null,
            afterItemId: isBelowNeighbour ? null : neighbours.afterItemId,
        },
    };
};

interface MoveTaskArgs {
    tasks: readonly DemoTask[];
    move: BoardMove<DemoTask>;
    view: TaskBoardView;
    todayDateOnly: string;
    /** The gap a task placed by hand copies from, if not the move's (`moveTasks`). */
    placeNeighbours?: BoardNeighbours;
    /** Where a task that copies the card above lands after, if not the move's `beforeItemId` (`moveTasks`). */
    landAfterItemId?: string | null;
}

const findMovedTask = ({
    tasks,
    move,
    view,
}: Pick<MoveTaskArgs, "tasks" | "move" | "view">) => {
    const index = tasks.findIndex((task) => task.id === move.item.id);
    return {
        index,
        moved: updateMovedTask(tasks[index] ?? move.item, move, view),
    };
};

/** A board move in manual order: the column and lane values change, the task lands between its neighbours. */
export const moveTaskBetweenNeighbours = ({
    tasks,
    move,
    view,
}: MoveTaskArgs) => {
    const { index, moved } = findMovedTask({ tasks, move, view });
    return insertTaskBetween({
        tasks,
        task: moved,
        neighbours: move,
        fallbackIndex: index,
    });
};

/**
 * A board move in sorted order: the values change, the sort places the task.
 * It goes right after the card above it, so among cards it ties with it
 * stays last, where the board showed it landing. A task placed by hand
 * (`isPlacedInSort`) also copies its neighbour's sort key
 * (`findTaskPlaceNeighbour`); the column and lane values still win.
 */
export const moveTaskInSortedOrder = ({
    tasks,
    move,
    view,
    todayDateOnly,
    placeNeighbours = move,
    landAfterItemId = move.beforeItemId,
}: MoveTaskArgs) => {
    const { index, moved } = findMovedTask({ tasks, move, view });
    const inheritSortKey = TASK_SORT_INHERITANCE[view.sort];
    const beside =
        move.isPlacedInSort && inheritSortKey !== null
            ? placeBeside({
                  neighbour: findTaskPlaceNeighbour({
                      sort: view.sort,
                      task: moved,
                      before: findTask(tasks, placeNeighbours.beforeItemId),
                      after: findTask(tasks, placeNeighbours.afterItemId),
                      todayDateOnly,
                  }),
                  task: moved,
                  placeNeighbours,
                  neighbours: {
                      beforeItemId: landAfterItemId,
                      afterItemId: move.afterItemId,
                  },
                  inheritSortKey,
              })
            : undefined;
    return insertTaskBetween({
        tasks,
        task: updateMovedTask(
            { ...(tasks[index] ?? move.item), ...beside?.sortKey },
            move,
            view,
        ),
        neighbours: beside?.neighbours ?? {
            beforeItemId: move.beforeItemId,
            afterItemId: null,
        },
        fallbackIndex: index,
    });
};

/**
 * The board's `canPlaceAt` for a sort with groups (`TASK_SORT_PLACE_GROUPS`):
 * a held drop goes beside a task of its group, or into an empty cell; undefined for other sorts.
 */
export const createTaskPlaceRule = ({
    view,
    todayDateOnly,
}: Pick<MoveTaskArgs, "view" | "todayDateOnly">) => {
    const getGroup = TASK_SORT_PLACE_GROUPS[view.sort];
    if (getGroup === null) {
        return undefined;
    }
    return ({
        item,
        from,
        to,
        beforeItem: before,
        afterItem: after,
    }: BoardPlaceCheck<DemoTask>): BoardMoveVerdict => {
        const task = updateMovedTask(item, { from, to }, view);
        const isAllowed =
            (before === null && after === null) ||
            findTaskPlaceNeighbour({
                sort: view.sort,
                task,
                before,
                after,
                todayDateOnly,
            }) !== null;
        return isAllowed
            ? { isAllowed: true }
            : {
                  isAllowed: false,
                  reason: `Place it among ${TASK_PLACE_GROUP_LABELS[getGroup(task, todayDateOnly)]} tasks`,
              };
    };
};

/**
 * A group drop's moves, applied in order with `moveTask`. A task placed by
 * hand copies its sort key from the cards either side of the whole run,
 * the gap `canPlaceAt` checked, never from a carried task that landed just
 * before it. It lands next to the card it copied, so it ties with it in the
 * gap whatever the list order: right before the card below, or after the
 * card above and the run's tasks already landed after it, so the run keeps
 * its order.
 */
export const moveTasks = ({
    moves,
    moveTask,
    ...args
}: Omit<MoveTaskArgs, "move" | "placeNeighbours" | "landAfterItemId"> & {
    moves: ReadonlyArray<BoardMove<DemoTask>>;
    moveTask: (args: MoveTaskArgs) => DemoTask[];
}) => {
    const gapsByItemId = new Map<string, BoardNeighbours>();
    const getOutside = (id: string | null, side: keyof BoardNeighbours) => {
        const carriedGap = id === null ? undefined : gapsByItemId.get(id);
        return carriedGap === undefined ? id : carriedGap[side];
    };
    const isLanded = (task: DemoTask | undefined) =>
        task !== undefined && gapsByItemId.has(task.id);
    const getLandedRunEnd = (tasks: readonly DemoTask[], id: string | null) => {
        let index = tasks.findIndex((task) => task.id === id);
        while (index !== -1 && isLanded(tasks[index + 1])) {
            index += 1;
        }
        return tasks[index]?.id ?? id;
    };
    return moves.reduce<DemoTask[]>(
        (tasks, move) => {
            const placeNeighbours = {
                beforeItemId: getOutside(move.beforeItemId, "beforeItemId"),
                afterItemId: getOutside(move.afterItemId, "afterItemId"),
            };
            const landAfterItemId = getLandedRunEnd(
                tasks,
                placeNeighbours.beforeItemId,
            );
            gapsByItemId.set(move.item.id, placeNeighbours);
            return moveTask({
                ...args,
                tasks,
                move,
                placeNeighbours,
                landAfterItemId,
            });
        },
        [...args.tasks],
    );
};

/** A blank To do task, like a quick create would make; `placeDraft` fills its cell's values. */
export const createDraftTask = (todayDateOnly: string): DemoTask => ({
    id: crypto.randomUUID(),
    title: "New task",
    stageId: null,
    status: TaskStatus.TODO,
    inProgressBy: "person",
    type: TaskType.QUESTION,
    priority: null,
    dueDateOnly: null,
    assigneeId: null,
    entityIds: [],
    clientId: null,
    estimateMinutes: null,
    commentCount: 0,
    closeName: null,
    servicePeriodDateOnly: null,
    createdById: null,
    createdDateOnly: todayDateOnly,
});

interface AddDraftTaskArgs {
    draft: DemoTask;
    target: BoardAddTarget;
    /** Sorted mode: what a gap `+` draft copies from its neighbour (`TASK_SORT_INHERITANCE`). */
    inheritSortKey?: TaskSortKeyInheritance;
}

/**
 * Places the draft between its neighbours. In sorted mode a header or footer
 * draft lands where the comparator puts it; a gap `+` draft copies the sort
 * key of the card above (below, for the gap above the first card) and goes
 * right beside it, so it ties with that card and shows in the gap.
 */
export const addDraftTask = (
    tasks: readonly DemoTask[],
    { draft, target, inheritSortKey }: AddDraftTaskArgs,
) => {
    const beside =
        target.button === "between" && inheritSortKey !== undefined
            ? placeBeside({
                  neighbour:
                      findTask(tasks, target.beforeItemId) ??
                      findTask(tasks, target.afterItemId),
                  task: draft,
                  placeNeighbours: target,
                  neighbours: target,
                  inheritSortKey,
              })
            : undefined;
    return insertTaskBetween({
        tasks,
        task: { ...draft, ...beside?.sortKey },
        neighbours: beside?.neighbours ?? target,
        fallbackIndex: tasks.length,
    });
};
